<template>
  <div>
    <div class="text-center mb-5">
      <h2 class="my-5">
        <strong v-if="settings.logo"
          >{{ $t("login.bienvenue") }} {{ settings.logo }}</strong
        >
        <strong v-else>{{ $t("login.title") }} </strong>
      </h2>
    </div>
    <div
      class="card mb-0"
      :class="$style.container"
      style="width: 100%; margin: auto"
    >
      <div class="text-dark font-size-24 mb-3 mt-0">
        <strong>{{ $t("login.connectezVous") }}</strong>
      </div>
      <p class="mt-0 mb-0">
        {{ $t("login.subscribePart1") }}
        <a
          class="terms-link"
          href="https://edtrust.tn/subscribe.php"
          target="_blank"
          ><b>{{ $t("login.subscribePart2") }}</b></a
        >
        {{ $t("login.subscribePart3") }}
      </p>
      <div class="mb-4"></div>
      <a-form class="mb-4" :form="form" @submit="handleSubmit" v-if="!showMFA && !qrCodeScanner">
        <a-form-item>
          <a-input
            size="large"
            :placeholder="$t('login.username')"
            v-decorator="[
              'userName',
              {
                rules: [
                  {
                    required: true,
                    message: $t('login.requisUsername'),
                  },
                ],
              },
            ]"
          />
        </a-form-item>
        <a-form-item class="mb-3">
          <a-input
            size="large"
            :placeholder="$t('profil.mdp')"
            type="password"
            v-decorator="[
              'password',
              {
                rules: [
                  {
                    required: true,
                    message: $t('login.requisMdp'),
                  },
                ],
              },
            ]"
          />
        </a-form-item>

        <a-checkbox class="mb-2 mt-0" @change="acceptedTerms = !acceptedTerms">
          {{ $t("login.readAndAccept") }}
          <a :href="termsLink" target="_blank" class="terms-link"
            ><b>{{ $t("login.termsAndConditions") }}.</b></a
          >
        </a-checkbox>
        <div class="d-flex justify-content-center gap-2">
          <a-button
            :type="acceptedTerms ? 'primary' : ''"
            htmlType="submit"
            size="large"
            class="text-center flex-grow-1"
            :disabled="!acceptedTerms"
            :loading="loading"
          >
            <strong>{{ $t("login.connextion") }}</strong>
          </a-button>
          <a-button
            size="large"
            :disabled="!acceptedTerms"
            @click="startScan"
            :title="$t('login.scanQR')"
          >
            <a-icon type="qrcode" style="font-size: 22px" />
          </a-button>
        </div>
      </a-form>

      <div v-if="qrCodeScanner && !showMFA" class="mb-4">
        <p>{{ $t("login.scanQR") }}</p>
        <div style="border: 2px solid #d9d9d9; border-radius: 4px; overflow: hidden">
          <qrcode-stream
            :track="paintBoundingBox"
            @decode="onQrDecode"
            @init="onQrInit"
          />
        </div>
        <a-button type="primary" size="large" class="w-100 mt-3" @click="stopScan">
          {{ $t("login.back") }}
        </a-button>
      </div>

      <!-- MFA Verification -->
      <div v-if="showMFA" class="mb-4">
        <a-alert
          :message="$t('mfa.verificationRequired')"
          type="info"
          show-icon
          class="mb-4"
        >
          <template slot="description">
            <div v-if="mfaMethod === 'app'">
              {{ $t("mfa.enterAppCode") }}
            </div>
            <div v-else-if="mfaMethod === 'sms'">
              {{ $t("mfa.enterSMSCode") }}
              <div v-if="userPhone" class="mt-2">
                <strong>{{ $t("mfa.sentTo") }}:</strong> {{ userPhone }}
              </div>
            </div>
          </template>
        </a-alert>
        <mfa-verify
          :mfa-token="mfaToken"
          :mfa-method="mfaMethod"
          @verified="handleMFAVerified"
        />
        <a-button
          type="link"
          @click="
            showMFA = false;
            mfaToken = null;
            acceptedTerms = false;
          "
          class="mt-2"
        >
          {{ $t("action.retour") }}
        </a-button>
      </div>
      <a-dropdown
        :trigger="['click']"
        placement="bottomCenter"
        style="margin: auto"
      >
        <div :class="$style.dropdown">
          <button
            type="button"
            class="btn btn-light dropdown-toggle dropdown-toggle-noarrow"
          >
            <i class="fe fe-more-horizontal" /><img
              style="border-radius: 25%; margin-right: 5px"
              width="20"
              :src="`resources/images/flags/${
                language == 'en'
                  ? 'united-states'
                  : language == 'fr'
                  ? 'france'
                  : language == 'sw'
                  ? 'kenya'
                  : 'tunisia'
              }.svg`"
              alt="Flag"
            />
            {{ getNativeLanguageName(language) }}
          </button>
        </div>

        <a-menu slot="overlay" @click="selectLocale">
          <a-menu-item key="en-US">
            <img
              style="border-radius: 25%; margin-right: 5px"
              width="20"
              src="resources/images/flags/united-states.svg"
              alt="Arabic"
            />
            English
          </a-menu-item>
          <a-menu-item key="fr-FR">
            <img
              style="border-radius: 25%; margin-right: 5px"
              width="20"
              src="resources/images/flags/france.svg"
              alt="Arabic"
            />
            Français
          </a-menu-item>
          <a-menu-item key="ar-AR">
            <img
              style="border-radius: 30%; margin-right: 5px"
              width="20"
              src="resources/images/flags/tunisia.svg"
              alt="Arabic"
            />
            عربية
          </a-menu-item>
          <a-menu-item key="sw-KE">
            <img
              style="border-radius: 25%; margin-right: 5px"
              width="20"
              src="resources/images/flags/kenya.svg"
              alt="Swahili"
            />
            Kiswahili
          </a-menu-item>
        </a-menu>
      </a-dropdown>
    </div>
  </div>
</template>
<script>
/* eslint-disable */
import { mapState } from "vuex";
import store from "store";
import router from "@/router";
import apiClient from "@/services/axios";
import MFAVerify from "../MFA/index.vue";
import { QrcodeStream } from "vue-qrcode-reader";
import {
  completeLoginFlow,
  getCurrentScholarYearId,
} from "@/services/loginHelper";

export default {
  name: "CuiLogin",
  components: {
    "mfa-verify": MFAVerify,
    MFAVerify,
    QrcodeStream,
  },
  computed: {
    ...mapState(["settings"]),
    loading() {
      return this.$store.state.user.loading;
    },
    language() {
      return this.settings.locale.substr(0, 2);
    },
  },
  data: function () {
    return {
      form: this.$form.createForm(this),
      termsLink: process.env.VUE_APP_TERMS_LINK,
      acceptedTerms: false,
      showMFA: false,
      mfaToken: null,
      mfaMethod: null,
      userPhone: null,
      qrCodeScanner: false,
      qrScanInProgress: false,
    };
  },
  methods: {
    startScan() {
      this.qrCodeScanner = true;
    },
    stopScan() {
      this.qrCodeScanner = false;
    },
    paintBoundingBox(detectedCodes, ctx) {
      for (const detectedCode of detectedCodes) {
        const {
          boundingBox: { x, y, width, height },
        } = detectedCode;
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#007bff";
        ctx.strokeRect(x, y, width, height);
      }
    },
    onQrInit(promise) {
      promise.catch((err) => {
        this.$notification.error({
          message: this.$t("login.qrCameraError"),
          description: err.message || String(err),
        });
        this.stopScan();
      });
    },
    onQrDecode(decodedString) {
      if (this.qrScanInProgress) return;
      this.qrScanInProgress = true;

      const deprecatedQRCodeRegex =
        /^([a-fA-F0-9]{24})\.([a-zA-Z0-9_]+)\.(https?:\/\/[^\s]+)$/;

      try {
        const decoded = window.atob(decodedString);
        if (deprecatedQRCodeRegex.test(decoded)) {
          this.$notification.error({
            message: this.$t("auth.loginFailed"),
            description: this.$t("login.oldQRCode"),
          });
          this.qrScanInProgress = false;
          return;
        }
        const [token] = decoded.split("#");
        if (!token) {
          throw new Error("invalid");
        }

        this.$store
          .dispatch("user/QR_LOGIN", { payload: { token } })
          .then(async (response) => {
            await this.processLoginResponse(response);
          })
          .catch(() => {})
          .finally(() => {
            this.qrScanInProgress = false;
          });
      } catch (e) {
        this.$notification.error({
          message: this.$t("auth.loginFailed"),
          description: this.$t("login.qrCodeInvalid"),
        });
        this.qrScanInProgress = false;
      }
    },
    async processLoginResponse(response) {
      if (response && response.mfaToken) {
        this.mfaToken = response.mfaToken;
        this.mfaMethod = response.mfaMethod;
        this.userPhone = response.phone;
        this.showMFA = true;
        this.stopScan();

        if (response.warning) {
          const { waitMinutes, message } = response.warning;
          let description = message;
          if (waitMinutes !== undefined) {
            description =
              waitMinutes === 1
                ? this.$t("mfa.rateLimitMessageSingular", {
                    minutes: waitMinutes,
                  })
                : this.$t("mfa.rateLimitMessage", { minutes: waitMinutes });
          }
          this.$notification.warning({
            message: this.$t("mfa.rateLimitExceeded"),
            description: description,
            duration: 8,
          });
        }
        return;
      }

      const { info, resourcesUrl } = response;
      const user = info;

      this.$store.dispatch("settings/SET_RESOURCES_URL", resourcesUrl, {
        root: true,
      });
      this.$store.commit("user/SET_STATE", {
        _id: user._id,
        userName: user.userName,
        building: user.building,
        status: user.status,
        role: user.role.role,
        roleId: user.role._id,
        access: user.role.access,
        avatar: user.avatar,
        authorized: true,
        edtrustAdmin: user.edtrustAdmin,
      });

      await this.$nextTick();

      await completeLoginFlow(
        this,
        user,
        resourcesUrl,
        apiClient,
        store,
        router,
        this.getFirstAcessPath
      );
    },
    changeAuthProvider(value) {
      this.$store.commit("settings/CHANGE_SETTING", {
        setting: "authProvider",
        value,
      });
    },
    selectLocale(item) {
      const setting = "locale";
      this.$store.commit("settings/CHANGE_SETTING", {
        setting,
        value: item.key,
      });
    },
    getNativeLanguageName(lang) {
      const languageNames = {
        en: "English",
        fr: "Français",
        ar: "عربية",
        sw: "Kiswahili",
      };
      return languageNames[lang] || "Français";
    },
    getCurrentScholarYearId,
    handleSubmit(e) {
      e.preventDefault();

      this.form.validateFields((err, values) => {
        if (!err) {
          this.$store
            .dispatch("user/LOGIN", { payload: values })
            .then(async (response) => {
              await this.processLoginResponse(response);
            })
            .catch(() => {
              this.form.resetFields();
            });
        }
      });
    },
    async handleMFAVerified(result) {
      // MFA verification successful, now proceed with normal login flow
      const { info, resourcesUrl } = result;
      const user = info;
      this.showMFA = false;
      this.mfaToken = null;

      // Update user state in Vuex store FIRST (this sets authorized: true)
      // This must happen before any navigation to prevent router guard redirects
      this.$store.dispatch("settings/SET_RESOURCES_URL", resourcesUrl, {
        root: true,
      });
      this.$store.commit("user/SET_STATE", {
        _id: user._id,
        userName: user.userName,
        building: user.building,
        status: user.status,
        role: user.role.role,
        roleId: user.role._id,
        access: user.role.access,
        avatar: user.avatar,
        authorized: true,
        edtrustAdmin: user.edtrustAdmin,
      });

      // Wait for Vue to process the state update before proceeding
      await this.$nextTick();

      // Use helper function to complete login flow
      await completeLoginFlow(
        this,
        user,
        resourcesUrl,
        apiClient,
        store,
        router,
        this.getFirstAcessPath
      );
    },
    getFirstAcessPath(access) {
      try {
        let firstAcess = {};
        let path = "";
        if (access.all === true || access.home === true) path = "/accueil";
        else {
          for (let key of Object.keys(access)) {
            if (access[key] === true || access[key].length > 0) {
              firstAcess = {
                key: key,
                role: Array.isArray(access[key])
                  ? access[key].filter((e) => e !== "all")
                  : access[key],
              };

              break;
            }
          }
          let routes = router.options.routes[0].children;
          path = routes.find(
            (e) =>
              (e.meta.accessGroup == firstAcess.key &&
                e.meta.roles.includes(firstAcess.role[0])) ||
              (e.meta.accessGroup == firstAcess.key && firstAcess.role === true)
          ).path;
        }
        return path;
      } catch (error) {
        return "/profile";
      }
    },
  },
};
</script>
<style lang="scss" module>
@import "@/components/cleanui/system/Auth/style.module.scss";
</style>

<style scoped>
.terms-link {
  color: #3583e9;
}

.terms-link:hover {
  text-decoration: underline;
}
</style>
